// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

interface Leaderboard {
  id: string;
  name: string;
  description: string;
  is_published: boolean;
  created_at: string;
  events?: {
    name: string;
  };
}

const LeaderboardsManagement: React.FC = () => {
  const [leaderboards, setLeaderboards] = useState<Leaderboard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeaderboards();
  }, []);

  const fetchLeaderboards = async () => {
    try {
      const { data, error } = await supabase
        .from('leaderboards')
        .select(`
          *,
          events(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeaderboards(data || []);
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
      toast.error('Failed to fetch leaderboards');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('leaderboards')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Leaderboard deleted successfully');
      fetchLeaderboards();
    } catch (error) {
      console.error('Error deleting leaderboard:', error);
      toast.error('Failed to delete leaderboard');
    }
  };

  const togglePublished = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('leaderboards')
        .update({ is_published: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      
      toast.success(`Leaderboard ${!currentStatus ? 'published' : 'unpublished'} successfully`);
      fetchLeaderboards();
    } catch (error) {
      console.error('Error updating leaderboard:', error);
      toast.error('Failed to update leaderboard');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-primary">Leaderboards Management</h1>
          <p className="text-muted-foreground">Create and manage dynamic leaderboards for events</p>
        </div>
        <Button onClick={() => navigate('/admin-dashboard/leaderboards/builder')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Leaderboard
        </Button>
      </div>

      {leaderboards.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <h3 className="text-lg font-semibold mb-2">No leaderboards found</h3>
            <p className="text-muted-foreground mb-4">Get started by creating your first leaderboard</p>
            <Button onClick={() => navigate('/admin-dashboard/leaderboards/builder')}>
              <Plus className="h-4 w-4 mr-2" />
              Create Leaderboard
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {leaderboards.map((leaderboard) => (
            <Card key={leaderboard.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {leaderboard.name}
                      <Badge variant={leaderboard.is_published ? "default" : "secondary"}>
                        {leaderboard.is_published ? "Published" : "Draft"}
                      </Badge>
                    </CardTitle>
                    {leaderboard.description && (
                      <p className="text-muted-foreground mt-1">{leaderboard.description}</p>
                    )}
                    {leaderboard.events && (
                      <Badge variant="outline" className="mt-2">
                        {leaderboard.events.name}
                      </Badge>
                    )}
                    {leaderboard.is_published && (
                      <div className="mt-3 p-2 bg-muted/50 rounded-md">
                        <p className="text-xs text-muted-foreground mb-1">Public URL:</p>
                        <code className="text-xs font-mono bg-background px-2 py-1 rounded border">
                          {window.location.origin}/leaderboard/{leaderboard.id}
                        </code>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin-dashboard/leaderboards/${leaderboard.id}/entries`)}
                      title="Enter athlete data"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Enter Data
                    </Button>
                    {leaderboard.is_published && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/leaderboard/${leaderboard.id}`, '_blank')}
                        title="View public leaderboard"
                      >
                        🌐 Public View
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin-dashboard/leaderboards/builder?edit=${leaderboard.id}`)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePublished(leaderboard.id, leaderboard.is_published)}
                    >
                      {leaderboard.is_published ? "Unpublish" : "Publish"}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(leaderboard.id, leaderboard.name)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaderboardsManagement;